import dayjs from "dayjs";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../../../components/buttons/button";
import { NumberCard } from "../../../../components/cards/numberCard";
import { PopoverMenu } from "../../../../components/dropdown/popover";
import { CaretDown } from "../../../../components/icon/downcaret";
import { Pill } from "../../../../components/pill";
import { TableComponent } from "../../../../components/table";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { useMonthlyDates } from "../../../../customeHooks/useMonthlyDates";
import {
  useGetSalesAgentEarningsSummaryQuery,
  useGetSalesAgentEventsQuery,
} from "../../../../redux/features/admin/sales/sales.api";
import { SalesAgentEventsQuery } from "../../../../redux/features/admin/sales/sales.types";
import {
  currencyFlags,
  currencyShorts,
  eventStatusPillMap,
} from "../../../../utils/constants";
import { formatDate, formatToCurrency } from "../../../../utils/helpers";

export const CommissionsBreakdown = ({ agentId }: { agentId: string }) => {
  const [query, setQuery] = useState<SalesAgentEventsQuery>({
    id: agentId,
    page: 1,
    limit: 20,
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    sortOrder: "asc",
  });

  const { months } = useMonthlyDates({
    initialDate: dayjs(),
    minDate: dayjs("2025-09-30"), //start from Oct 2025
    maxDate: dayjs(
      `${new Date().getFullYear()}-${new Date().getMonth() + 2}-01`,
    ),
  });

  const { data: agentEarnings, isLoading: loadingAgentEarnings } =
    useGetSalesAgentEarningsSummaryQuery(
      {
        id: agentId,
      },
      { refetchOnMountOrArgChange: true },
    );

  const { data: agentEvents, isFetching: loadingAgentEvents } =
    useGetSalesAgentEventsQuery(
      {
        ...query,
        startDate: dayjs(`${query.year}-${query.month}-01`)
          .startOf("month")
          .format("YYYY-MM-DD"),
        endDate: dayjs(`${query.year}-${query.month}-01`)
          .endOf("month")
          .format("YYYY-MM-DD"),
      },
      { refetchOnMountOrArgChange: true },
    );
  return (
    <>
      <BgWrapper className="grid grid-cols-1 gap-[10px] rounded-none md:grid-cols-4">
        <NumberCard
          value={agentEarnings?.data?.monthlyEarningsNGN ?? 0}
          text="Earnings (This Month)"
          currency="NGN"
          isCurrency
          loading={loadingAgentEarnings}
          titleFirst
        />
        <NumberCard
          value={agentEarnings?.data?.monthlyTicketsSold ?? 0}
          text="Tickets Sold (This Month)"
          isCurrency={false}
          loading={loadingAgentEarnings}
          titleFirst
        />

        <NumberCard
          value={agentEarnings?.data?.totalEarningsNGN ?? 0}
          text="Total Earnings"
          currency="NGN"
          isCurrency
          loading={loadingAgentEarnings}
          titleFirst
        />

        <NumberCard
          value={agentEarnings?.data?.totalTicketsSold ?? 0}
          text="Total Tickets Sold"
          isCurrency={false}
          loading={loadingAgentEarnings}
          titleFirst
        />
      </BgWrapper>

      <BgWrapper className="flex flex-col gap-3 rounded-t-none">
        <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
          <PopoverMenu
            menuItems={[...months].reverse().map((month) => {
              return {
                name: formatDate(month.start.toString(), "MMMM YYYY"),
                value: "",
                onClick: () => {
                  setQuery({
                    ...query,
                    page: 1,
                    month: month.start.month() + 1,
                    year: month.start.year(),
                  });
                },
              };
            })}
            menuClassName="!min-w-[230px] shadow-md !rounded-[8px]"
            menuItemsClassName="!font-bold !py-4"
            position="bottom start"
            closeOnClick
          >
            <Button
              type="button"
              text={`${formatDate(`${query.year}-${query.month}-01`, "MMMM YYYY")}`}
              className="!border-0 !p-0 !px-0 !py-0"
              variation="plain"
              extra={<CaretDown />}
            />
          </PopoverMenu>

          <div className="grid w-full grid-cols-2 items-center gap-3 md:flex md:w-fit md:flex-row">
            <Button
              type="button"
              onClick={() => {
                if (query.sortOrder === "asc") {
                  setQuery({ ...query, sortOrder: "desc" });
                } else {
                  setQuery({ ...query, sortOrder: "asc" });
                }
              }}
              text="Date Modified"
              className="!min-w-fit !rounded-2xl !p-3 !text-sm"
              icon={query.sortOrder === "asc" ? "order-asc" : "order-desc"}
              variation="grey"
            />

            <Button
              type="button"
              onClick={async () => {
                // await exportData({
                //   type: "users",
                //   fetchAll: true,
                //   sortOrder: query.sortOrder,
                //   search: query.search || undefined,
                //   searchTerm: query.search || undefined,
                // });
              }}
              text="Export"
              className="!min-w-fit !rounded-2xl !p-3 !text-sm"
              icon="export"
              variation="grey"
              //   loading={exportingData}
            />
          </div>
        </div>

        <TableComponent
          headers={[]}
          rows={
            agentEvents?.data?.data?.map((event) => {
              return {
                id: event._id,
                items: [
                  <div
                    key={`${event.eventName}-${event._id}`}
                    className="flex flex-wrap items-center gap-2 text-grey-600"
                  >
                    <img
                      src={event.image.replace("http:", "https:")}
                      alt={`${event.eventName}-banner`}
                      className="block aspect-square size-[40px] rounded-sm"
                    />

                    <p className="flex-1">
                      <Link
                        to={`/events/${event._id}`}
                        className="font-bold text-grey-900"
                      >
                        {event.eventName} {currencyFlags[event.currency]}
                      </Link>{" "}
                      ∙ {event.ticketsSold} Tickets Sold{" "}
                      <strong>
                        (
                        {formatToCurrency(event.totalCommission, {
                          currency: currencyShorts[event.currency],
                        })}
                        )
                      </strong>
                    </p>

                    <Pill
                      text={eventStatusPillMap[event.status].text}
                      variation={eventStatusPillMap[event.status].variant}
                    />

                    <p>Created by {event.organizer}</p>
                  </div>,
                ],
              };
            }) ?? []
          }
          emptyImage="/assets/images/calendar-3d.png"
          emptyTitle="No Events"
          isTableEmpty={[0, undefined].includes(
            agentEvents?.data?.data?.length,
          )}
          isPaginated
          itemsPerPage={query.limit}
          isLoading={loadingAgentEvents}
          currentPage={query.page}
          totalCount={agentEvents?.data?.totalCount}
          onPageChange={(page) => {
            setQuery({
              ...query,
              page,
            });
          }}
        />
      </BgWrapper>
    </>
  );
};
